import React, { useEffect, useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import style from "./index.module.scss";
import { DropdownTriangle } from "./DropdownTriangle";
import { useSession } from "~/modules/Profile/session";

interface UserMenuProps {
  userName: string;
  userEmail: string;
  userRole?: string;
  onProfileClick: () => void;
  onSignOut: () => void;
}

type ThemeMode = "light" | "dark";

const getInitials = (name: string): string => {
  if (!name) {
    return "";
  }
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) {
    return "";
  }
  const first = parts[0]?.[0] ?? "";
  const second = parts[1]?.[0] ?? parts[0]?.[1] ?? "";
  return (first + second).toUpperCase();
};

export const UserMenu = ({
  userName,
  userEmail,
  userRole,
  onProfileClick,
  onSignOut,
}: UserMenuProps) => {
  const [theme, setTheme] = useState<ThemeMode>("light");

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    const saved = window.localStorage.getItem("theme");
    const initial = saved === "dark" ? "dark" : "light";
    setTheme(initial);
    if (initial === "dark") {
      document.documentElement.setAttribute("data-theme", "dark");
    } else {
      document.documentElement.removeAttribute("data-theme");
    }
  }, []);

  const toggleTheme = () => {
    setTheme((prev) => {
      const next: ThemeMode = prev === "dark" ? "light" : "dark";
      if (next === "dark") {
        document.documentElement.setAttribute("data-theme", "dark");
      } else {
        document.documentElement.removeAttribute("data-theme");
      }
      window.localStorage.setItem("theme", next);
      return next;
    });
  };

  const initials = getInitials(userName);

  const { session } = useSession();

  return (
    <DropdownMenu.Root>
      <div className={style.userMenu}>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className={style.userMenuToggle}
          >
            <div className={style.userMenuAvatar} aria-hidden="true">
              {initials}
            </div>
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className={style.userMenuPanel}
            align="end"
            sideOffset={16}
            alignOffset={-12}
          >
            <DropdownTriangle className={style.menuTriangle} />
            <div className={style.userMenuEmail} title={userEmail}>
              {userEmail}
            </div>
            <div className={style.userMenuDivider} />
            <DropdownMenu.Item asChild>
              <button
                type="button"
                className={style.userMenuRow}
                onClick={toggleTheme}
              >
                <i
                  className={[
                    "far",
                    theme === "dark" ? "fa-toggle-on" : "fa-toggle-off",
                    style.userMenuRowIcon,
                    style.userMenuRowIconToggle,
                  ].join(" ")}
                  aria-hidden="true"
                />
                <span className={style.userMenuRowLabel}>Dark mode</span>
              </button>
            </DropdownMenu.Item>
            <DropdownMenu.Item asChild>
              <button
                type="button"
                className={style.userMenuRow}
                onClick={onProfileClick}
              >
                <i
                  className={["far", "fa-user-circle", style.userMenuRowIcon].join(" ")}
                  aria-hidden="true"
                />
                <span className={style.userMenuRowLabel}>Account settings</span>
              </button>
            </DropdownMenu.Item>
            <div className={style.userMenuDivider} />
            <DropdownMenu.Item asChild>
              <button
                type="button"
                className={style.userMenuRow}
                onClick={onSignOut}
              >
                <i
                  className={["far", "fa-power-off", style.userMenuRowIcon].join(" ")}
                  aria-hidden="true"
                />
                <span className={style.userMenuRowLabel}>Logout</span>
              </button>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </div>
    </DropdownMenu.Root>
  );
};
